import { useRef, useState } from 'react';
import { compressToWebP } from '@/lib/imageUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  FileText, Upload, Download, Trash2, Loader2, UploadCloud,
  FileImage, FileType, File as FileIcon,
} from 'lucide-react';
import { PERMISSIONS, type EmployeeDocument } from '@pms/shared';
import { staffDocumentsService } from '@/services/staff-documents.service';
import { formatDateDMY } from '@/lib/dateFormat';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

const MAX_BYTES = 15 * 1024 * 1024;
const ALLOWED = new Set([
  'application/pdf',
  'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return formatDateDMY(iso);
}

function fileIcon(mime: string) {
  if (mime.startsWith('image/')) return { Icon: FileImage, cls: 'text-violet-600', bg: 'bg-violet-50' };
  if (mime === 'application/pdf') return { Icon: FileText, cls: 'text-rose-600', bg: 'bg-rose-50' };
  if (mime.includes('word')) return { Icon: FileType, cls: 'text-teal-600', bg: 'bg-teal-50' };
  return { Icon: FileIcon, cls: 'text-muted-foreground', bg: 'bg-muted' };
}

export function StaffDocumentsTab({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.STAFF_DOCUMENTS_MANAGE);

  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<{ id: string; name: string } | null>(null);

  const { data: docs, isLoading, isError, refetch } = useQuery({
    queryKey: ['staff-documents', userId],
    queryFn: () => staffDocumentsService.list(userId),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => staffDocumentsService.upload(userId, file),
    onSuccess: () => {
      toast.success('Document uploaded');
      void qc.invalidateQueries({ queryKey: ['staff-documents', userId] });
    },
    onError: () => toast.error('Upload failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (docId: string) => staffDocumentsService.remove(docId),
    onSuccess: () => {
      toast.success('Document deleted');
      void qc.invalidateQueries({ queryKey: ['staff-documents', userId] });
    },
    onError: () => toast.error('Delete failed'),
  });

  async function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED.has(file.type)) {
      toast.error('Only PDF, image, or Word documents are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File exceeds the 15MB size limit');
      return;
    }
    const fileToUpload = await compressToWebP(file);
    uploadMut.mutate(fileToUpload);
  }

  async function handleDownload(doc: EmployeeDocument) {
    try {
      await staffDocumentsService.download(doc.id, doc.original_name);
    } catch {
      toast.error('Download failed');
    }
  }

  const list = docs ?? [];

  return (
    <Card>
      <SectionHeader
        icon={<FileText />}
        title="Documents"
        action={
          canManage ? (
            <Button size="sm" disabled={uploadMut.isPending} onClick={() => fileRef.current?.click()}>
              {uploadMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload
            </Button>
          ) : undefined
        }
      />
      <CardContent className="space-y-4 p-4">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
        />

        {/* Drop zone */}
        {canManage && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-border/60 bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
            }`}
          >
            <UploadCloud className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Drag &amp; drop or click to upload</p>
            <p className="text-xs text-muted-foreground">PDF, Image, or Word document (max 15MB)</p>
          </div>
        )}

        {/* List / states */}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/40" />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-lg border bg-rose-50/50 p-6 text-center text-sm text-rose-600">
            Failed to load documents.
            <button className="ml-2 underline" onClick={() => void refetch()}>Retry</button>
          </div>
        ) : list.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No documents uploaded yet</p>
        ) : (
          <div className="space-y-2">
            {list.map((doc) => {
              const { Icon, cls, bg } = fileIcon(doc.mime_type);
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 rounded-lg border border-border/40 bg-card px-3 py-2.5 transition-colors hover:bg-muted/30"
                >
                  <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${bg}`}>
                    <Icon className={`h-[18px] w-[18px] ${cls}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{doc.original_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatSize(doc.size_bytes)} · {formatDate(doc.created_at)}
                      {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => void handleDownload(doc)}
                    title="Download"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  {canManage && (
                    <button
                      onClick={() => setPendingDeleteDoc({ id: doc.id, name: doc.original_name })}
                      disabled={deleteMut.isPending}
                      title="Delete"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={!!pendingDeleteDoc}
        onOpenChange={(o) => { if (!o) setPendingDeleteDoc(null); }}
        title={`Delete "${pendingDeleteDoc?.name}"?`}
        description="This document will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteDoc) deleteMut.mutate(pendingDeleteDoc.id); }}
      />
    </Card>
  );
}
